import { useState, useCallback } from 'react';
import { useApi } from 'api';
import { useConfirmAction } from './useConfirmAction';
import { logger } from '../utils/logger';

export interface UseInvoiceActionsOptions {
  onDeleteSuccess?: () => void;
  onFinalizeSuccess?: () => void;
  onTogglePaidSuccess?: () => void;
}

export interface UseInvoiceActionsResult {
  deleteAction: ReturnType<typeof useConfirmAction<number>>;
  finalizeAction: ReturnType<typeof useConfirmAction<number>>;
  togglePaid: (invoiceId: number, currentPaidStatus: boolean) => Promise<void>;
  isTogglingPaid: boolean;
  togglingPaidId: number | null;
}

export function useInvoiceActions(options: UseInvoiceActionsOptions = {}): UseInvoiceActionsResult {
  const api = useApi();
  const { onDeleteSuccess, onFinalizeSuccess, onTogglePaidSuccess } = options;
  
  const [togglingPaidId, setTogglingPaidId] = useState<number | null>(null);
  
  const deleteAction = useConfirmAction<number>(
    async (id: number) => {
      await api.deleteInvoice({ id });
    },
    {
      onSuccess: onDeleteSuccess,
      onError: () => alert('Failed to delete invoice. Please try again.'),
    }
  );
  
  const finalizeAction = useConfirmAction<number>(
    async (id: number) => {
      await api.putInvoice({ id }, { invoice: { id, finalized: true } });
    },
    {
      onSuccess: onFinalizeSuccess,
      onError: () => alert('Failed to finalize invoice. Please try again.'),
    }
  );
  
  const togglePaid = useCallback(async (invoiceId: number, currentPaidStatus: boolean) => {
    setTogglingPaidId(invoiceId);
    
    try {
      await api.putInvoice(
        { id: invoiceId },
        { invoice: { id: invoiceId, paid: !currentPaidStatus } }
      );
      onTogglePaidSuccess?.();
    } catch (err) {
      logger.error('Error updating paid status:', err);
      alert('Failed to update paid status. Please try again.');
    } finally {
      setTogglingPaidId(null);
    }
  }, [api, onTogglePaidSuccess]);
  
  return {
    deleteAction,
    finalizeAction,
    togglePaid,
    isTogglingPaid: togglingPaidId !== null,
    togglingPaidId,
  };
}

export default useInvoiceActions;
